import { useAppConfig } from "../AppConfigProvider";
import {
  useGetCoursesSummaryBySchoolQuery,
  useGetStudentsSummaryBySchoolQuery,
  useGetCourseCompletionDetailsQuery,
  useGetCourseAnalyticsByIdQuery,
  useGetDegreesByDegreeIdQuery,
  useGetDegreesBySchoolByQueryQuery,
  useGetCoursesByDegreeByQueryQuery,
  useGetStudentDetailedAnalyticsQuery,
} from "@axio/graphql/generated/graphql";

export type AnalyticsEntityType = "degree" | "course" | "student";

const isDefined = <T>(value: T | null | undefined): value is T => value != null;

export const useAnalyticsData = ({
  searchQuery,
  page,
  pageSize,
  sortBy,
  activeEntityType,
  activeEntityId,
}: {
  searchQuery: string;
  page: number;
  pageSize: number;
  sortBy: string;
  activeEntityType: AnalyticsEntityType;
  activeEntityId: string | null;
}) => {
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0] ?? "";

  const activeCourseId = activeEntityType === "course" ? activeEntityId : null;
  const activeDegreeId = activeEntityType === "degree" ? activeEntityId : null;
  const activeStudentId = activeEntityType === "student" ? activeEntityId : null;

  const shouldFetchDegreeList = activeEntityType === "degree" && !activeEntityId;
  const shouldFetchCourseList = activeEntityType === "course" && !activeEntityId;
  const shouldFetchStudentList = activeEntityType === "student" && !activeEntityId;

  const {
    data: coursesData,
    loading: coursesLoading,
    error: coursesError,
  } = useGetCoursesSummaryBySchoolQuery({
    variables: { schoolId, searchQuery, page, pageSize, sortBy },
    skip: !schoolId || !shouldFetchCourseList,
  });

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetStudentsSummaryBySchoolQuery({
    variables: { schoolId, searchQuery, page, pageSize, sortBy },
    skip: !schoolId || !shouldFetchStudentList,
  });

  const {
    data: courseCompletionDetailsData,
    loading: courseCompletionDetailsLoading,
    error: courseCompletionDetailsError,
  } = useGetCourseCompletionDetailsQuery({
    variables: { courseId: activeCourseId ?? "" },
    skip: !activeCourseId,
  });

  const {
    data: detailedCourseData,
    loading: courseDetailLoading,
    error: courseDetailError,
  } = useGetCourseAnalyticsByIdQuery({
    variables: { courseId: activeCourseId ?? "" },
    skip: !activeCourseId,
  });

  const {
    data: selectedDegreeData,
    loading: selectedDegreeLoading,
    error: selectedDegreeError,
  } = useGetDegreesByDegreeIdQuery({
    variables: { id: activeDegreeId ?? "" },
    skip: !activeDegreeId,
  });

  const {
    data: degreesData,
    loading: degreesLoading,
    error: degreesError,
  } = useGetDegreesBySchoolByQueryQuery({
    variables: { schoolId, searchQuery, page, pageSize, sortBy },
    skip: !schoolId || !shouldFetchDegreeList,
  });

  const {
    data: coursesByDegreeData,
    loading: coursesByDegreeLoading,
    error: coursesByDegreeError,
  } = useGetCoursesByDegreeByQueryQuery({
    variables: { degreeId: activeDegreeId ?? "", searchQuery: "", page: 1, pageSize: 10, sortBy: "name_asc" },
    skip: !activeDegreeId,
  });

  const {
    data: studentDetailedAnalyticsData,
    loading: studentDetailedAnalyticsLoading,
    error: studentDetailedAnalyticsError,
  } = useGetStudentDetailedAnalyticsQuery({
    variables: { studentId: activeStudentId ?? "", schoolId },
    skip: !activeStudentId || !schoolId,
  });

  const listLoading =
    (shouldFetchDegreeList && degreesLoading) ||
    (shouldFetchCourseList && coursesLoading) ||
    (shouldFetchStudentList && studentsLoading);

  const listError = shouldFetchDegreeList
    ? degreesError
    : shouldFetchCourseList
      ? coursesError
      : shouldFetchStudentList
        ? studentsError
        : undefined;

  const detailLoading =
    (!!activeDegreeId && (selectedDegreeLoading || coursesByDegreeLoading)) ||
    (!!activeCourseId && (courseDetailLoading || courseCompletionDetailsLoading)) ||
    (!!activeStudentId && studentDetailedAnalyticsLoading);

  const detailError = activeDegreeId
    ? selectedDegreeError || coursesByDegreeError
    : activeCourseId
      ? courseDetailError || courseCompletionDetailsError
      : activeStudentId
        ? studentDetailedAnalyticsError
        : undefined;

  const isLoading = listLoading || detailLoading;
  const degreesListData = degreesData?.getDegreesBySchoolByQuery?.data;
  const normalizedDegreesData = {
    ...degreesListData,
    degrees: degreesListData?.degrees?.filter(isDefined) ?? [],
    totalCount: degreesListData?.totalCount ?? 0,
  };

  return {
    coursesData: coursesData?.getCoursesSummaryBySchool,
    studentsData: studentsData?.getStudentsSummaryBySchool,
    completionData: courseCompletionDetailsData?.getCourseCompletionDetails?.data ?? [],
    detailedCourse: detailedCourseData?.getCourseAnalyticsById?.data?.[0] || null,
    degreesData: normalizedDegreesData,
    selectedDegree: selectedDegreeData?.getDegree?.data?.find(isDefined) ?? null,
    coursesByDegreeData: coursesByDegreeData?.getCoursesByDegreeByQuery?.data,
    coursesByDegreeCourses: coursesByDegreeData?.getCoursesByDegreeByQuery?.data?.courses?.filter(isDefined) ?? [],
    studentDetailedAnalyticsData: studentDetailedAnalyticsData?.getStudentDetailedAnalytics?.data?.[0] || null,
    isLoading,
    listLoading,
    listError,
    detailLoading,
    detailError,
  };
};
